/** 每个文档内独立去重的两个分析脚本。 */
export type AnalyticsScript = 'sensors' | 'classic';

/** 固定沿用现有脚本地址；Classic 只能由身份就绪后的编排器调用。 */
const SCRIPT_URLS: Record<AnalyticsScript, string> = {
  sensors: '/d770591a7.js',
  classic: '/3532c8eb.js',
};

/** 一个脚本的等待结果及可撤销监听。 */
interface ScriptEntry {
  promise: Promise<void>;
  cancel: () => void;
}

/** 文档销毁后保持终态，禁止迟到回调或新调用恢复加载。 */
interface DocumentScripts {
  disposed: boolean;
  entries: Map<AnalyticsScript, ScriptEntry>;
}

/** 按文档保存有限状态，避免跨文档复用已销毁的脚本。 */
const documents = new WeakMap<Document, DocumentScripts>();

/** Classic在接管前使用数组，接管后暴露带push方法的命令处理器。 */
interface ClassicCommandQueue {
  push: (...commands: unknown[]) => unknown;
}
/** 保持Classic标准可调用全局形状，不在bootstrap中初始化Tracker。 */
interface ClassicNamespace {
  (...commands: unknown[]): void;
  q: ClassicCommandQueue;
}

/** 仅在身份门禁之后准备Classic必需的namespace；已接管实例直接复用。 */
function prepareClassicNamespace(): boolean {
  const owner = window as unknown as {
    GlobalSnowplowNamespace?: string[];
    snowplow?: ClassicNamespace;
  };
  const current = owner.snowplow;
  if (
    typeof current === 'function' &&
    current.q &&
    !Array.isArray(current.q) &&
    typeof current.q.push === 'function'
  )
    return true;
  // 明确script error后的重试保留原队列及namespace，不重复登记。
  owner.GlobalSnowplowNamespace ??= [];
  if (!owner.GlobalSnowplowNamespace.includes('snowplow'))
    owner.GlobalSnowplowNamespace.push('snowplow');
  if (typeof owner.snowplow !== 'function') {
    /** 在真实bundle接管前缓存标准Classic命令。 */
    const enqueue = (...commands: unknown[]): void => {
      enqueue.q.push(commands);
    };
    enqueue.q = [] as unknown[] | ClassicCommandQueue;
    owner.snowplow = enqueue;
  }
  owner.snowplow.q ??= [];
  return false;
}

/** 复用 pending/resolved Promise；只有明确加载失败才允许下一次重试。 */
export function loadAnalyticsScript(kind: AnalyticsScript): Promise<void> {
  if (typeof document === 'undefined')
    return Promise.reject(new Error('analytics_document_missing'));
  const ownerDocument = document;
  let state = documents.get(ownerDocument);
  if (!state) {
    state = { disposed: false, entries: new Map() };
    documents.set(ownerDocument, state);
  }
  if (state.disposed) return Promise.reject(new Error('analytics_script_loader_disposed'));
  const existing = state.entries.get(kind);
  if (existing) return existing.promise;

  // 先登记状态再操作DOM，兼容同步触发load的测试或宿主环境。
  let resolve!: () => void;
  let reject!: (error: Error) => void;
  const promise = new Promise<void>((onResolve, onReject) => {
    resolve = onResolve;
    reject = onReject;
  });
  let settled = false;
  let script: HTMLScriptElement | undefined;
  const entry: ScriptEntry = { promise, cancel };
  state.entries.set(kind, entry);

  /** 只解除本站点附加的监听，保留旧入口自己的处理器。 */
  function detach(): void {
    script?.removeEventListener('load', onLoad);
    script?.removeEventListener('error', onError);
  }
  /** load仅表示脚本完成；神策初始化及身份由独立门禁确认。 */
  function onLoad(): void {
    if (settled || state!.disposed) return;
    settled = true;
    detach();
    resolve();
  }
  /** 明确失败清除节点与单例记录，交给编排器决定重试时机。 */
  function onError(): void {
    if (settled || state!.disposed) return;
    settled = true;
    detach();
    script?.remove();
    state!.entries.delete(kind);
    reject(new Error('analytics_script_load_failed'));
  }
  /** 文档销毁取消等待，但不通过删除pending节点触发第二次加载。 */
  function cancel(): void {
    detach();
    if (settled) return;
    settled = true;
    reject(new Error('analytics_script_loader_disposed'));
  }

  try {
    // Classic bootstrap随已通过身份门禁的动态加载执行，SSR不提前声明。
    if (kind === 'classic' && prepareClassicNamespace()) {
      settled = true;
      resolve();
      return promise;
    }
    const url = SCRIPT_URLS[kind];
    // 集成切换前复用layout的相同URL节点，不能并行插入覆盖SDK全局对象。
    script = Array.from(ownerDocument.querySelectorAll<HTMLScriptElement>('script[src]')).find(
      (candidate) => candidate.src === new URL(url, ownerDocument.baseURI).href,
    );
    const needsInsertion = !script;
    script ??= ownerDocument.createElement('script');
    script.setAttribute('data-zl-analytics', kind);
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    if (needsInsertion) {
      script.async = true;
      script.src = url;
      ownerDocument.head.appendChild(script);
    }
  } catch {
    onError();
  }
  return promise;
}

/** 页面销毁时取消本站点监听并忽略迟到回调。 */
export function disposeAnalyticsScriptLoader(): void {
  if (typeof document === 'undefined') return;
  let state = documents.get(document);
  if (!state) {
    state = { disposed: true, entries: new Map() };
    documents.set(document, state);
  }
  state.disposed = true;
  for (const entry of Array.from(state.entries.values())) entry.cancel();
}
